import { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '@/lib/api';
import MobileShell from '@/components/MobileShell';
import BroadcastStatus from '@/components/broadcast/BroadcastStatus';
import PreferenceFallback from '@/components/broadcast/PreferenceFallback';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  fetchBooking,
  fetchServiceRequest,
  hasAutoRedirected,
  markAutoRedirected,
  paymentStillRequired,
  resolveBookingForRequest,
  shouldNavigateToPayment,
} from '@/lib/acceptedBooking';

const TERMINAL = new Set([
  'cancelled',
  'expired',
  'failed_no_providers',
  'completed',
  'assigned',
]);

export default function BroadcastSearchingScreen() {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const [request, setRequest] = useState(null);
  const [acceptedBookingId, setAcceptedBookingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigatingRef = useRef(false);

  useEffect(() => {
    let active = true;
    let intervalId = null;

    const tick = async () => {
      if (!active || navigatingRef.current) return;
      const req = await fetchServiceRequest(requestId).catch(() => null);
      if (!active) return;
      if (!req) return;
      setRequest(req);
      setLoading(false);

      // The request payload carries no booking_id; once the backend reports
      // the request accepted, resolve the booking from the consumer bookings
      // list via service_request_id (server data only — no local inference).
      const bookingId =
        req.status === 'assigned' ? await resolveBookingForRequest(requestId) : null;
      if (!active) return;

      if (bookingId) {
        setAcceptedBookingId(bookingId);
        // Attempt the automatic hop once per request per app session to avoid
        // redirect loops if the customer backs out of the payment screen.
        if (hasAutoRedirected(requestId) || navigatingRef.current) return;
        navigatingRef.current = true;
        markAutoRedirected(requestId);
        // Route decision comes from fresh backend data only — never from the
        // stale poll snapshot. Booking fetch failure just skips the hop.
        const booking = await fetchBooking(bookingId).catch(() => null);
        if (!active) return;
        if (booking && shouldNavigateToPayment(req, booking)) {
          navigate(`/consumer/bookings/${bookingId}/payment`, { replace: true });
          return;
        }
        if (booking && !paymentStillRequired(booking)) {
          const target =
            booking.status === 'completed' || booking.status === 'cancelled'
              ? `/consumer/bookings/${bookingId}`
              : `/consumer/bookings/${bookingId}/tracking`;
          navigate(target, { replace: true });
          return;
        }
        navigatingRef.current = false;
        return;
      }

      if (TERMINAL.has(req.status) || req.status === 'preference_fallback_pending') {
        if (intervalId) clearInterval(intervalId);
      }
    };

    tick();
    intervalId = setInterval(tick, 3000);
    return () => {
      active = false;
      if (intervalId) clearInterval(intervalId);
    };
  }, [requestId, navigate]);

  const cancel = async () => {
    try {
      await api.post(`/service-requests/${requestId}/cancel`);
      toast.success('Request cancelled');
      navigate('/consumer/home');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not cancel');
    }
  };

  if (loading && !request) {
    return (
      <MobileShell title="Searching">
        <div className="p-5 text-slate-500 text-sm">Loading…</div>
      </MobileShell>
    );
  }

  if (!request) {
    return (
      <MobileShell title="Searching">
        <div className="p-5 text-slate-500 text-sm">Request not found.</div>
      </MobileShell>
    );
  }

  const showFallback = request.status === 'preference_fallback_pending';

  // Accepted-state card: shown while the customer is still on the search
  // screen after a provider accepted. The manual CTA is the fallback path —
  // the automatic redirect in the poll effect handles the normal path.
  const showAcceptedCard = Boolean(acceptedBookingId);

  return (
    <MobileShell title="Searching">
      <div className="px-5 py-5 space-y-4" data-testid="broadcast-searching-screen">
        <BroadcastStatus request={request} />

        {showFallback && (
          <PreferenceFallback
            requestId={requestId}
            statusMessage={request.status_message}
            onUpdated={(req) => setRequest(req)}
            onCancel={() => navigate('/consumer/home')}
          />
        )}

        {showAcceptedCard && (
          <div className="resqly-card p-4 space-y-3" data-testid="accepted-booking-card">
            <div className="text-sm font-semibold text-emerald-700">
              A verified responder accepted your request
            </div>
            <p className="text-sm text-slate-600">
              Please complete your payment to confirm the booking.
            </p>
            <Button
              data-testid="accepted-go-to-payment"
              className="w-full bg-slate-900 hover:bg-slate-800"
              onClick={() =>
                navigate(`/consumer/bookings/${acceptedBookingId}/payment`, { replace: true })
              }
            >
              Go to payment
            </Button>
          </div>
        )}

        {!showFallback && !showAcceptedCard && request.status === 'assigned' && (
          <div className="resqly-card p-4 text-sm text-emerald-700 bg-emerald-50">
            A verified responder has been assigned. We will share more details in the app soon.
          </div>
        )}

        {!showFallback && !TERMINAL.has(request.status) && (
          <p className="text-sm text-slate-500 text-center">
            Searching for verified responders near you…
          </p>
        )}

        {!showFallback && request.broadcast_active && (
          <Button
            data-testid="broadcast-cancel-request"
            variant="outline"
            onClick={cancel}
            className="w-full"
          >
            Cancel request
          </Button>
        )}
      </div>
    </MobileShell>
  );
}
